// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { uniqBy } from "lodash";
import { ReactNode, useMemo } from "react";

import { Column, GroupedCombobox } from "@hightouch/htui";

import { MinimalParentModel } from "src/components/audiences/types";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import {
  ColumnReference,
  ColumnValue,
  FilterableColumn,
  PropertyCondition,
  ValueType,
  isColumnReference,
} from "src/types/visual";

import { ErrorMessage } from "./error-message";
import {
  columnsToGroupedOptions,
  createJourneyEventColumn,
  getModelIdFromColumn,
  getPropertyNameFromProperty,
  getTraitDefinitionIdFromColumn,
  getTraitPropertyType,
} from "./utils";
import { getPropertyLabel } from "src/components/explore/filter-popover/utils";
import {
  ColumnType,
  getPropertyType,
  JsonArrayOperator,
} from "@hightouch/lib/query/visual/types";
import { useFilterableColumnsQuery } from "src/graphql";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import type { TraitDefinitionWithoutFilterableColumnsFragment } from "src/graphql";

export const PropertySelector = ({
  error,
  condition,
  isDisabled = false,
  value,
  onChange,
  /**
   * TODO(nolan): the only place this is used should be time inputs in
   * edge cases like window filter. We should switch those over to use filterable options context
   * and then remove this prop.
   */
  columns: columnsOverride,
  parent,
  /** Does nothing. We started building out property comparison to parent model properties but ended up not implemnting it */
  includeParentModelProperties = true,
  removePortal,
  // selectTraits is used to indicate whether traits are selected
  selectingTraits = false,
}: {
  error?: ReactNode;
  condition: PropertyCondition;
  value?: ColumnValue | undefined;
  onChange: (value: ColumnValue | undefined) => void;
  isDisabled?: boolean;
  columns?: FilterableColumn[] | undefined;
  parent: MinimalParentModel | undefined;
  includeParentModelProperties?: boolean;
  removePortal?: boolean;
  selectingTraits?: boolean;
}) => {
  const { enableReferencingEntryEvent, selectedEvent } =
    useQueryBuilderContext();
  const {
    columns: allColumns,
    events,
    parents,
    traits,
    filterableColumnsByModelId,
  } = useFilterableOptionsContext();

  const journeyEventModelId = enableReferencingEntryEvent
    ? String(selectedEvent?.eventModelId)
    : "";

  // The backend only supports referencing entry events on 1 layer below the root
  const immediateParent = parents[parents.length - 1];
  const rootEvents = immediateParent?.events ?? events;

  const selectedEventModel = enableReferencingEntryEvent
    ? rootEvents.find(
        (event) => String(event.to_model.id) === journeyEventModelId,
      )
    : undefined;

  // Some journey conditions are able to reference their entry event in addition to parent model properties
  const { data: journeyEventColumns = [], isLoading: journeyEventsAreLoading } =
    useFilterableColumnsQuery(
      {
        segmentId: journeyEventModelId,
      },
      {
        enabled:
          enableReferencingEntryEvent &&
          Boolean(selectedEventModel) &&
          Boolean(journeyEventModelId), // only fetch if an event is selected
        select: (data) =>
          data.segments_by_pk?.filterable_audience_columns ?? [],
      },
    );

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const columns = useMemo(
    () =>
      uniqBy(
        [
          ...(columnsOverride ?? []),
          ...(allColumns ?? []),
          ...journeyEventColumns,
        ],
        (el) =>
          `modelId:${el.model_id},relId:${el.column_reference?.path?.at(-1)},name:${el.name}`,
      ),
    [
      columnsOverride,
      allColumns,
      includeParentModelProperties,
      journeyEventColumns,
    ],
  );

  /** Generally, we only want to rename the parent model name to "Properties" in the drop down if we are querying from the parent model */
  const doNotRenameParent = useMemo(
    () =>
      parent &&
      columnsOverride &&
      columnsOverride.length > 0 &&
      columnsOverride.every((c) => String(c.model_id) !== String(parent.id)),
    [parent, columnsOverride],
  );

  const { property, operator } = condition;
  const propertyType = getPropertyType(condition);

  // Get the LHS trait definition ID to exclude it from RHS options
  const lhsTraitDefinitionId = isColumnReference(property)
    ? getTraitDefinitionIdFromColumn(property)
    : null;

  // Filter traits by type compatibility with LHS value, excluding the LHS trait itself
  const compatibleTraits = useMemo(() => {
    if (!traits) return [];

    return traits.filter((trait) => {
      if (trait.id === lhsTraitDefinitionId) return false;

      // Derive trait type from filterableColumns (for occurrence traits) or config (for others)
      const traitFilterableColumns =
        filterableColumnsByModelId[trait.relationship?.to_model?.id] ?? [];
      const traitType = getTraitPropertyType(trait, traitFilterableColumns);

      if (
        [JsonArrayOperator.Contains, JsonArrayOperator.DoesNotContain].includes(
          operator,
        )
      ) {
        if (propertyType === ColumnType.JsonArrayStrings) {
          return traitType === ColumnType.String;
        } else if (propertyType === ColumnType.JsonArrayNumbers) {
          return traitType === ColumnType.Number;
        }
      }

      return traitType === propertyType;
    });
  }, [
    traits,
    propertyType,
    operator,
    filterableColumnsByModelId,
    lhsTraitDefinitionId,
  ]);

  // Filter regular columns by type compatibility. Memoized because
  // `columns` can hold thousands of entries on high-cardinality
  // workspaces and the result feeds into the expensive groupBy + map +
  // sort below.
  const filteredColumns = useMemo(
    () =>
      (columns ?? []).filter((c) =>
        isColumnCompatibleWithOperation(c, {
          propertyType,
          operator,
          property,
        }),
      ),
    [columns, property, operator, propertyType],
  );

  // Memoized: at workspace scale the groupBy + per-model map + sort runs
  // O(N×M) on the main thread. Without this it ran on every render of
  // PropertySelector and was the dominant cause of "freezes when picking
  // a column" for high-cardinality workspaces.
  const options = useMemo(
    () =>
      columnsToGroupedOptions(selectingTraits ? [] : filteredColumns, {
        traits: selectingTraits ? compatibleTraits : [],
        columnToOption: transformColumnToOption(journeyEventModelId),
        traitToOption: (trait) =>
          transformTraitToOption(trait, filterableColumnsByModelId),
        modelLabel: ({ modelName, modelId, relationshipName }) => {
          if (journeyEventModelId) {
            if (journeyEventModelId === modelId) {
              return "Entry Event Columns";
            }
          }
          if (!doNotRenameParent && parent?.id) {
            if (modelId === String(parent.id)) {
              // traits are grouped under parent model name in dropdown
              return selectingTraits
                ? `Calculated from: ${parent.name}`
                : "Properties";
            }
          }
          const label = relationshipName
            ? `${modelName} (via ${relationshipName})`
            : modelName;
          return selectingTraits ? `Calculated from: ${label}` : label;
        },
      }),
    [
      selectingTraits,
      filteredColumns,
      compatibleTraits,
      journeyEventModelId,
      filterableColumnsByModelId,
      doNotRenameParent,
      parent?.id,
      parent?.name,
    ],
  );

  return (
    <Column sx={{ width: "auto", flex: "0 0 auto" }}>
      <GroupedCombobox
        removePortal={removePortal}
        isDisabled={isDisabled}
        optionGroups={options}
        isLoading={journeyEventsAreLoading}
        placeholder={selectingTraits ? "Select trait…" : "Select property…"}
        width="2xs"
        value={value}
        onChange={onChange}
      />
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </Column>
  );
};

/** Returns true if `column` is a valid RHS comparison candidate for the given LHS context. */
export function isColumnCompatibleWithOperation(
  column: FilterableColumn,
  {
    propertyType,
    operator,
    property,
  }: {
    propertyType: ColumnType | null | undefined;
    operator: PropertyCondition["operator"];
    property: PropertyCondition["property"];
  },
): boolean {
  const effectiveType = column.custom_type ?? column.type;

  if (
    [JsonArrayOperator.Contains, JsonArrayOperator.DoesNotContain].includes(
      operator,
    )
  ) {
    if (propertyType === ColumnType.JsonArrayStrings) {
      return effectiveType === ColumnType.String;
    } else if (propertyType === ColumnType.JsonArrayNumbers) {
      return effectiveType === ColumnType.Number;
    }
  }

  const modelId = isColumnReference(property) && getModelIdFromColumn(property);
  return (
    effectiveType === propertyType &&
    !(
      // Filter out the left operand's column (want to be sure it's
      // from the same model since we also show columns from other
      // merge models)
      (
        column.model_id == modelId &&
        column.name === getPropertyNameFromProperty(property)
      )
    )
  );
}

const transformColumnToOption =
  (journeyEventModelId?: string) => (c: FilterableColumn) => ({
    label: getPropertyLabel(c.alias, c.name),
    value: {
      type: ValueType.Column,
      property:
        journeyEventModelId &&
        String(c.model_id) === String(journeyEventModelId)
          ? createJourneyEventColumn({
              name: c.name,
              modelId: journeyEventModelId,
            })
          : c.column_reference,
      propertyType: (c.custom_type ?? c.type) as ColumnType,
    } as ColumnValue,
  });

const transformTraitToOption = (
  trait: TraitDefinitionWithoutFilterableColumnsFragment,
  filterableColumnsByModelId: Record<string, FilterableColumn[]>,
) => {
  // Derive trait type from filterableColumns (for occurrence traits) or config (for others)
  const traitFilterableColumns =
    filterableColumnsByModelId[trait.relationship?.to_model?.id ?? ""] ?? [];
  const traitType = getTraitPropertyType(trait, traitFilterableColumns);

  const traitColumn: ColumnReference = {
    type: "trait",
    traitDefinitionId: trait.id,
    conditions: [],
    name: trait.name,
  };

  // Match LHS logic from getInitialTraitColumn:
  // - Non-formula traits with relationship -> RelatedColumn
  // - Everything else (formula traits or no relationship) -> TransformedColumn
  // This ensures RHS and LHS use consistent wrapping
  const property =
    trait.type !== "formula" && trait.relationship && trait.relationship.id
      ? {
          type: "related" as const,
          path: [trait.relationship.id],
          column: traitColumn,
        }
      : {
          type: "transformed" as const,
          column: traitColumn,
        };

  return {
    label: trait.name,
    value: {
      type: ValueType.Column,
      property,
      propertyType: traitType,
    } as ColumnValue,
  };
};
